import { DatePickerProps } from '@mui/x-date-pickers/DatePicker/DatePicker.types'
import React, { useCallback, useState } from 'react'
import {
  DatePicker as MUIDatePicker,
  DateTimePicker as MuiDateTimePicker,
} from '@mui/x-date-pickers'
import { InputAdornment } from '@common/material/InputAdornment'
import { Clear } from '@mui/icons-material'
import { IconButton } from '@common/material/IconButton'
import { DateTimePickerProps } from '@mui/x-date-pickers'
import { TextInput } from './TextInput'
import moment from 'moment'

type DatePicker<TDate, T extends boolean> = (T extends false | undefined
  ? DatePickerProps<TDate>
  : DateTimePickerProps<TDate>) &
  React.RefAttributes<HTMLDivElement> & {
    required?: boolean
    error?: boolean
    withClearButton?: boolean
    withTime?: T
    UTC?: boolean
    dataTest?: string
    name?: string
    disablePast?: boolean
    showInputTime?: boolean
  }

export const DatePicker: <TDate, WithTime extends boolean>(
  props: DatePicker<TDate, WithTime>,
) => React.ReactElement = ({
  slotProps,
  slots,
  error,
  required,
  withTime,
  withClearButton,
  UTC,
  dataTest,
  disabled,
  disablePast,
  showInputTime,
  ...rest
}) => {
  const [open, setOpen] = useState(false)
  const handleClear = useCallback(() => {
    // @ts-ignore
    rest.onChange(null)
  }, [])

  const Component = withTime ? MuiDateTimePicker : MUIDatePicker

  if (Constants.E2E || Constants.INTEGRATION_TEST) {
    const { name, label, value, sx } = rest
    return (
      <TextInput
        {...{ 'data-test': dataTest, name, sx }}
        {...slotProps?.textField}
        inputProps={{ 'data-test': dataTest + '-input' }}
        label={label || name}
        value={value ? moment(value).format('DD MMM yyyy') : ''}
        onChange={(e) => {
          const date = Utils.safeParseEventValue(e)

          if (!isNaN(new Date(date).valueOf())) {
            // @ts-ignore
            rest.onChange(new Date(date))
          } else {
            // @ts-ignore
            rest.onChange(undefined)
          }
        }}
      />
    )
  }

  return (
    <Component
      format={`DD MMM YYYY${withTime || showInputTime ? ' HH:mm' : ''}`}
      ampm={false}
      disabled={disabled}
      timezone={UTC ? 'UTC' : undefined}
      open={open}
      disablePast={disablePast}
      onClose={() => setOpen(false)}
      slotProps={{
        ...slotProps,
        textField: {
          onClick: () => setOpen(true),
          // @ts-ignore
          readOnly: true,
          required: required,
          error: error,
          ...slotProps?.textField,
        },
        actionBar: {
          actions: ['clear'],
          ...slotProps?.actionBar,
        },
      }}
      // @ts-ignore
      slots={{
        inputAdornment:
          withClearButton && !disabled
            ? () => (
                <InputAdornment position='end'>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClear()
                    }}
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            : () => null,
        ...slots,
      }}
      {...rest}
    />
  )
}
